import { TFunction } from 'i18next';

/**
 * Extrae parámetros del mensaje de error original
 * Ejemplo: "No se encontró el pedido con ID 123" -> { id: 123 }
 * Ejemplo: "Invalid email format: test@invalid" -> { email: "test@invalid" }
 */
const extractParamsFromMessage = (message: string): Record<string, string | number> => {
  const params: Record<string, string | number> = {};

  // Extraer IDs de diferentes formatos
  const idPatterns = [
    /ID\s+(\d+)/i,
    /id\s+(\d+)/i,
    /con ID (\d+)/i,
    /with ID (\d+)/i,
    /\{id\}/i,
    /\{(\d+)\}/i,
  ];

  for (const pattern of idPatterns) {
    const match = message.match(pattern);
    if (match) {
      const value = parseInt(match[1] || match[0].replace(/[{}]/g, ''), 10);
      if (!isNaN(value)) {
        params.id = value;
        break;
      }
    }
  }

  // Extraer product_id
  const productIdMatch = message.match(/producto con ID (\d+)|product with ID (\d+)|product_id[:\s]+(\d+)/i);
  if (productIdMatch) {
    params.product_id = parseInt(productIdMatch[1] || productIdMatch[2] || productIdMatch[3], 10);
  }

  // Extraer pack_id
  const packIdMatch = message.match(/pack con ID (\d+)|pack with ID (\d+)|pack_id[:\s]+(\d+)/i);
  if (packIdMatch) {
    params.pack_id = parseInt(packIdMatch[1] || packIdMatch[2] || packIdMatch[3], 10);
  }

  // Extraer email del formato "Invalid email format: {email}"
  const emailMatch = message.match(/Invalid email format:\s*(.+?)(?:\s|$)/i);
  if (emailMatch) {
    params.email = emailMatch[1].trim();
  }

  // Extraer currency del formato "Invalid currency: {code}"
  const currencyMatch = message.match(/Invalid currency:\s*(.+?)(?:\s|$)/i);
  if (currencyMatch) {
    params.code = currencyMatch[1].trim();
  }

  // Extraer índice de línea del formato "lines.0.quantity" -> { lineIndex: 0 }
  const lineIndexMatch = message.match(/lines\.(\d+)\./i);
  if (lineIndexMatch) {
    params.lineIndex = parseInt(lineIndexMatch[1], 10) + 1; // +1 para mostrar índice humano (1-based)
  }

  return params;
};

/**
 * Mapea mensajes de error de validación de Laravel a claves de traducción
 */
const mapValidationErrorToKey = (
  message: string,
  field: string
): string | null => {
  console.log('🔍 mapValidationErrorToKey - Entrada:', {
    message,
    field,
  });

  const lowerMessage = message.toLowerCase();
  const lowerField = field.toLowerCase();

  console.log('🔍 mapValidationErrorToKey - Normalizado:', {
    lowerMessage,
    lowerField,
  });

  // Errores de campos requeridos
  if (lowerMessage.includes('obligatorio') || lowerMessage.includes('required')) {
    console.log('🔍 mapValidationErrorToKey - Es campo requerido, verificando campo específico...');
    
    if (lowerField === 'id') {
      console.log('🔍 mapValidationErrorToKey - Mapeado a: validation.id.required');
      return 'validation.id.required';
    }
    if (lowerField.includes('lines') && lowerField.includes('quantity')) {
      console.log('🔍 mapValidationErrorToKey - Mapeado a: validation.lines.quantity.required');
      return 'validation.lines.quantity.required';
    }
    if (lowerField.includes('lines') && lowerField.includes('amount')) {
      console.log('🔍 mapValidationErrorToKey - Mapeado a: validation.lines.amount.required');
      return 'validation.lines.amount.required';
    }
    if (lowerField.includes('customer.email') || lowerMessage.includes('customer.email')) {
      console.log('🔍 mapValidationErrorToKey - Mapeado a: validation.customer.email.required');
      return 'validation.customer.email.required';
    }
    if (lowerField.includes('customer.address.country_code') || lowerField.includes('country_code') || lowerMessage.includes('country_code')) {
      console.log('🔍 mapValidationErrorToKey - Mapeado a: validation.customer.address.country_code.required');
      return 'validation.customer.address.country_code.required';
    }
    if (lowerField.includes('total_tax')) {
      console.log('🔍 mapValidationErrorToKey - Mapeado a: validation.total_tax.required');
      return 'validation.total_tax.required';
    }
    if (lowerField.includes('total_discount')) {
      console.log('🔍 mapValidationErrorToKey - Mapeado a: validation.total_discount.required');
      return 'validation.total_discount.required';
    }
    if (lowerField.includes('total_shipping')) {
      console.log('🔍 mapValidationErrorToKey - Mapeado a: validation.total_shipping.required');
      return 'validation.total_shipping.required';
    }
    if (lowerField.includes('lines')) {
      console.log('🔍 mapValidationErrorToKey - Mapeado a: validation.lines.required');
      return 'validation.lines.required';
    }
    if (lowerField.includes('customer')) {
      console.log('🔍 mapValidationErrorToKey - Mapeado a: validation.customer.required');
      return 'validation.customer.required';
    }
    
    console.log('🔍 mapValidationErrorToKey - No se encontró mapeo específico para campo requerido');
  }

  // Errores de tipo de dato
  if (lowerMessage.includes('debe ser un número entero') || lowerMessage.includes('must be an integer')) {
    if (lowerField === 'id') return 'validation.id.must_be_integer';
    if (lowerField.includes('lines') && lowerField.includes('quantity')) return 'validation.lines.quantity.must_be_integer';
    if (lowerField.includes('lines') && lowerField.includes('id')) return 'validation.lines.id.must_be_integer';
    if (lowerField.includes('product_id')) return 'validation.lines.product_id.must_be_integer';
    if (lowerField.includes('pack_id')) return 'validation.lines.pack_id.must_be_integer';
    if (lowerField.includes('payment_method_id')) return 'validation.payment_method_id.must_be_integer';
  }

  // Errores de valor mínimo
  if (lowerMessage.includes('debe ser al menos') || lowerMessage.includes('must be at least')) {
    if (lowerField.includes('lines') && lowerField.includes('quantity')) return 'validation.lines.quantity.min';
    if (lowerField.includes('lines') && lowerField.includes('amount')) return 'validation.lines.amount.min';
    if (lowerMessage.includes('quantity must be at least 1')) return 'validation.lines.quantity.min_value_object';
  }

  // Errores de formato
  if (lowerMessage.includes('debe ser numérico') || lowerMessage.includes('must be numeric')) {
    if (lowerField.includes('lines') && lowerField.includes('amount')) return 'validation.lines.amount.must_be_numeric';
    if (lowerField.includes('total_tax')) return 'validation.total_tax.must_be_numeric';
    if (lowerField.includes('total_discount')) return 'validation.total_discount.must_be_numeric';
    if (lowerField.includes('total_shipping')) return 'validation.total_shipping.must_be_numeric';
  }

  if (lowerMessage.includes('debe ser al menos 0') || lowerMessage.includes('must be at least 0')) {
    if (lowerField.includes('total_tax')) return 'validation.total_tax.min';
    if (lowerField.includes('total_discount')) return 'validation.total_discount.min';
    if (lowerField.includes('total_shipping')) return 'validation.total_shipping.min';
    if (lowerField.includes('amount must be a positive number')) return 'validation.lines.amount.positive';
  }

  // Errores de formato de email
  if (lowerMessage.includes('correo válido') || lowerMessage.includes('valid email') || lowerMessage.includes('invalid email format') || lowerMessage.includes('no es un correo válido') || lowerMessage.includes('is not a valid email')) {
    // Verificar que el campo sea customer.email para mapear correctamente
    if (lowerField.includes('customer.email') || lowerField === 'customer.email' || lowerMessage.includes('customer.email')) {
      return 'validation.customer.email.invalid_format';
    }
    // Si es otro campo con email, también mapearlo
    if (lowerField.includes('email')) {
      return 'validation.customer.email.invalid_format';
    }
  }

  // Errores de formato de currency
  if (lowerMessage.includes('invalid currency')) {
    return 'validation.currency.invalid';
  }

  // Errores de array/conjunto
  if (lowerMessage.includes('debe tener al menos 1 elementos') || lowerMessage.includes('must have at least 1 elements')) {
    return 'validation.lines.min_elements';
  }
  if (lowerMessage.includes('debe ser un conjunto') || lowerMessage.includes('must be an array')) {
    if (lowerField.includes('lines')) return 'validation.lines.must_be_array';
    if (lowerField.includes('customer')) return 'validation.customer.must_be_array';
  }

  // Errores de ID inválido
  if (lowerMessage.includes('seleccionado es inválido') || lowerMessage.includes('selected is invalid')) {
    if (lowerField === 'id') return 'validation.id.invalid';
    if (lowerField.includes('lines') && lowerField.includes('id')) return 'validation.lines.id.invalid';
    if (lowerField.includes('product_id')) return 'validation.lines.product_id.invalid';
    if (lowerField.includes('pack_id')) return 'validation.lines.pack_id.invalid';
    if (lowerField.includes('payment_method_id')) return 'validation.payment_method_id.invalid';
    if (lowerField.includes('country_code')) return 'validation.customer.address.country_code.invalid';
  }

  console.log('🔍 mapValidationErrorToKey - No se encontró mapeo, retornando null');
  return null;
};

/**
 * Traduce un error de la API a un mensaje internacionalizado
 * 
 * @param error - Error de la API (puede ser error de axios o respuesta de la API)
 * @param t - Función de traducción de i18next
 * @returns Mensaje traducido
 */
export const translateOrderApiError = (error: any, t: TFunction): string => {
  console.log('🔍 errorTranslator - Inicio traducción:', {
    error,
    errorType: typeof error,
    errorMessage: error?.message,
    errorResponse: error?.response,
    errorResponseData: error?.response?.data,
    errorResponseStatus: error?.response?.status,
    errorStatusCode: error?.statusCode,
    errorStatus: error?.status,
    errorData: error?.data,
  });

  // Obtener datos de la respuesta
  const responseData = error?.response?.data || error?.data || {};
  const statusCode = error?.response?.status || error?.statusCode || error?.status;

  console.log('🔍 errorTranslator - Datos extraídos:', {
    responseData,
    statusCode,
    hasErrors: !!responseData?.errors,
    errorsType: typeof responseData?.errors,
    errorsIsArray: Array.isArray(responseData?.errors),
    directMessage: responseData?.error?.message || responseData?.message,
    hasErrorCode: !!responseData?.error?.error_code,
  });

  // Si hay un objeto error con error_code (errores de dominio)
  if (responseData?.error?.error_code) {
    const errorObj = responseData.error;
    const errorCode = errorObj.error_code;
    const originalMessage = errorObj.message || '';
    const field = errorObj.field || '';

    // Intentar traducir usando el error_code
    const translationKey = `errors.domain.${errorCode}`;
    const translated = t(translationKey);

    // Si se encontró traducción
    if (translated && translated !== translationKey) {
      // Extraer parámetros del mensaje original
      const params = extractParamsFromMessage(originalMessage);

      // Reemplazar placeholders en la traducción
      let finalMessage = translated;
      if (Object.keys(params).length > 0) {
        Object.keys(params).forEach(key => {
          const regex = new RegExp(`\\{${key}\\}`, 'g');
          finalMessage = finalMessage.replace(regex, String(params[key]));
        });
      }

      return finalMessage;
    }
  }

  // Si hay errores de validación (422)
  if (statusCode === 422 && responseData?.errors) {
    console.log('🔍 errorTranslator - Procesando errores 422:', {
      errors: responseData.errors,
      errorsType: typeof responseData.errors,
      errorsIsArray: Array.isArray(responseData.errors),
    });

    const errors = responseData.errors;

    // Si es un objeto con campos específicos
    if (typeof errors === 'object' && !Array.isArray(errors)) {
      const errorMessages: string[] = [];

      // Procesar cada campo con errores
      Object.entries(errors).forEach(([field, messages]) => {
        console.log('🔍 errorTranslator - Procesando campo:', {
          field,
          messages,
          messagesType: typeof messages,
          messagesIsArray: Array.isArray(messages),
        });

        const messageArray = Array.isArray(messages) ? messages : [messages];
        
        messageArray.forEach((msg: string) => {
          console.log('🔍 errorTranslator - Procesando mensaje:', {
            field,
            msg,
            msgType: typeof msg,
          });

          // Intentar mapear el error a una clave de traducción
          const translationKey = mapValidationErrorToKey(msg, field);
          
          console.log('🔍 errorTranslator - Mapeo de traducción:', {
            field,
            msg,
            translationKey,
          });
          
          if (translationKey) {
            const params = extractParamsFromMessage(msg);
            // translationKey ya incluye 'validation.', solo agregamos 'errors.'
            const fullTranslationKey = `errors.${translationKey}`;
            const translated = t(fullTranslationKey);
            
            console.log('🔍 errorTranslator - Traducción:', {
              translationKey,
              fullTranslationKey,
              translated,
              translatedEqualsKey: translated === fullTranslationKey,
              params,
            });
            
            // Si no se encontró traducción específica, usar el mensaje original
            if (!translated || translated === fullTranslationKey) {
              console.log('🔍 errorTranslator - No se encontró traducción, usando mensaje original');
              errorMessages.push(msg);
            } else {
              // Reemplazar placeholders
              let finalMessage = translated;
              Object.keys(params).forEach(key => {
                const regex = new RegExp(`\\{${key}\\}`, 'g');
                finalMessage = finalMessage.replace(regex, String(params[key]));
              });
              console.log('🔍 errorTranslator - Mensaje final traducido:', finalMessage);
              errorMessages.push(finalMessage);
            }
          } else {
            // Si no se puede mapear, usar el mensaje original
            console.log('🔍 errorTranslator - No se pudo mapear, usando mensaje original');
            errorMessages.push(msg);
          }
        });
      });

      if (errorMessages.length > 0) {
        const result = errorMessages.join('\n');
        console.log('🔍 errorTranslator - Resultado final (422):', result);
        return result;
      }
    }
  }

  // Si hay un mensaje de error directo en error.message
  const directErrorMessage = responseData?.error?.message || responseData?.message;
  
  console.log('🔍 errorTranslator - Mensaje directo:', {
    directErrorMessage,
    hasDirectMessage: !!directErrorMessage,
  });
  
  if (directErrorMessage) {
    const originalMessage = directErrorMessage;

    console.log('🔍 errorTranslator - Procesando mensaje directo:', {
      originalMessage,
    });

    // Intentar mapear mensajes conocidos
    const translationKey = mapValidationErrorToKey(originalMessage, '');
    
    console.log('🔍 errorTranslator - Mapeo mensaje directo:', {
      originalMessage,
      translationKey,
    });
    
    if (translationKey) {
      const params = extractParamsFromMessage(originalMessage);
      // translationKey ya incluye 'validation.', solo agregamos 'errors.'
      const fullTranslationKey = `errors.${translationKey}`;
      const translated = t(fullTranslationKey);
      
      console.log('🔍 errorTranslator - Traducción mensaje directo:', {
        translationKey,
        fullTranslationKey,
        translated,
        translatedEqualsKey: translated === fullTranslationKey,
        params,
      });
      
      if (translated && translated !== fullTranslationKey) {
        let finalMessage = translated;
        Object.keys(params).forEach(key => {
          const regex = new RegExp(`\\{${key}\\}`, 'g');
          finalMessage = finalMessage.replace(regex, String(params[key]));
        });
        console.log('🔍 errorTranslator - Mensaje directo traducido:', finalMessage);
        return finalMessage;
      }
    }

    // Verificar si es un error de ValueObject
    if (originalMessage.includes('Invalid email format:')) {
      const params = extractParamsFromMessage(originalMessage);
      return t('errors.validation.customer.email.invalid_format_value_object', {
        email: params.email || '',
      });
    }
    if (originalMessage.includes('Invalid currency:')) {
      const params = extractParamsFromMessage(originalMessage);
      return t('errors.validation.currency.invalid_value_object', {
        code: params.code || '',
      });
    }
    if (originalMessage.includes('Quantity must be at least 1')) {
      return t('errors.validation.lines.quantity.min_value_object');
    }
    if (originalMessage.includes('amount must be a positive number')) {
      return t('errors.validation.lines.amount.positive');
    }

    return originalMessage;
  }

  // Fallback: mensaje genérico
  return t('errors.generic', 'Error inesperado al procesar la solicitud');
};
